import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Radio, Card, Row, Col, Modal, Input, Button, Alert, Checkbox } from 'antd';
import './custom-styles.css';
import SimpleReactValidator from 'simple-react-validator';
import { SpError } from '../../widgetDesign';
import { useDispatch, useSelector } from 'react-redux';
import { OnLoadOtp, OtpValidate, ResendOtp } from './EndPoints';
import SupportingFields from './SupportingFields';
import { useForm } from 'react-hook-form';
import { checkPaymentTypeExist, MethodIcon, segmentOptions } from './helper';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { Text, SpanText } from '../../design';
import { setSavedPaymentsClose } from '../../redux/directory';

const SavedPaymentMethods = ({
  buttonText, submit, isCaptchaEnabled, sitekey, supportedPaymentTypes, surchargeEnabled, surchargeType, updateSurcharge, accountSupportedPaymentTypes,
  serviceFee, serviceFeeRate, discount, subTotal, totalPayable, amount, prefix, tax, serviceFeeVal, surcharge, tip, surchareNotification,
  showAutopayCheckbox, autopay, setAutopay,
}) => {
  const [, forceUpdate] = useState();
  const [selectedBank, setSelectedBank] = useState(null);
  const [otp, setOtp] = useState('');
  const [recaptchaToken, setRecaptchToken] = useState(null);
  const [showCaptchError, setShowCaptchError] = useState(null);
  const [surchargeOptional, setSurchargeOptional] = useState(surchargeType === 'OPTIONAL');
  const { customerDetails } = useSelector((state) => state.customerDetailsInfo);
  const { directoryLookUp, directoryAccessToken, otpLoader, directoryPaymentMethods } = useSelector((state) => state.directory);
  const [otpModal, setOtpModal] = useState(!directoryAccessToken);
  const { bankExist, cardExist } = checkPaymentTypeExist(supportedPaymentTypes);
  const previousValues = useRef({ selectedBank: null, surchargeOptional: null });

  const dispatch = useDispatch();

  const filteredSegmentOptions = useMemo(() => {
    return segmentOptions.filter(option =>
      supportedPaymentTypes?.includes(option.key)
    );
  }, [supportedPaymentTypes]);
  
  const { errors, control, setValue, reset, watch, handleSubmit } = useForm({
    defaultValues: {
      street: '',
      city: '',
      postalCode: '',
      country: '',
      state: '',
      bankType: '',
    }
  });

  const simpleValidator = useRef(new SimpleReactValidator());

  useEffect(() => {
    dispatch(OnLoadOtp(customerDetails));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (directoryAccessToken) {
      setOtpModal(false);
    };
  }, [directoryAccessToken]);

  useEffect(() => {
    if (!selectedBank || typeof updateSurcharge !== 'function') return;

    const isSame =
      previousValues.current.selectedBank === selectedBank &&
      previousValues.current.surchargeOptional === surchargeOptional;

    if (isSame) return;

    previousValues.current = {
      selectedBank,
      surchargeOptional,
    };

    updateSurcharge(surchargeOptional, selectedBank);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBank, surchargeOptional]);

  const handleOk = () => {
    const formValid = simpleValidator.current.allValid();

    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }
    dispatch(OtpValidate({
      otpId: directoryLookUp?.otpRequestGid,
      otp: otp,
      directoryUserGid: directoryLookUp?.directoryUserGid,
      phoneNumber: customerDetails?.phoneNumber,
      bankExist,
      cardExist,
      paymentMethodTypes: accountSupportedPaymentTypes,
    }));
  };

  const handleResend = () => {
    dispatch(ResendOtp({
      otpId: directoryLookUp?.otpRequestGid,
      phoneNumber: customerDetails?.phoneNumber,
    }));
  }

  const onClickPay = (data) => {
    if (recaptchaToken) {
      setShowCaptchError(null);
    } else {
      setShowCaptchError('Captcha is required');
    }

    const selectedBankData = directoryPaymentMethods?.find((item) => item?.gid === selectedBank);
    const payload = {
      type: "DIRECTORY",
      postalCode: data?.postalCode,
      directory: {
        directoryUserGid: directoryLookUp?.directoryUserGid,
        directoryPaymentMethodGid: selectedBank,
        type: selectedBankData?.type,
        cvv: data?.cvv || null,
        directoryToken: directoryAccessToken
      },
      subType : data?.bankType || null,
      paymentMethodBillingAddress: {
          street: data?.address, 
          city: data?.city,
          state: data?.state, 
          countryCode: data?.country
      },
      recaptchaToken,
    }
    submit(payload);
  };

  const customSubmitButtonStyle = {
    width: '100%',
    backgroundColor: '#0b64bc',
    fontSize: '15px',
    fontWeight: '600',
  };

  return (
    <>
      {directoryPaymentMethods?.length > 0 && (
        <div style={{ padding: '24px', background: '#f5f5f5', borderRadius: '10px' }}>
          <div className="hide-scrollbar">
            <Radio.Group 
              onChange={(e) => setSelectedBank(e.target.value)} 
              value={selectedBank}
              style={{ width: '100%' }}
            >
              <Row gutter={[16, 16]}>
                {directoryPaymentMethods?.map((method) => {
                  const isCard = method?.type === 'CARD';

                  const title = isCard
                    ? method?.card?.brand
                    : method?.bank?.bankName;

                  const subText = isCard
                    ? `${method?.card?.expiryMonth} / ${method?.card?.expiryYear}`
                    : method?.bank?.routingNumber;

                  const lastFour = isCard
                    ? method?.card?.lastFour
                    : method?.bank?.lastFour;

                  const key = method?.gid; // single source of truth

                  return (
                    <Col xs={24} sm={24} md={12} key={key}>
                      <Card
                        hoverable
                        className={`bank-card ${selectedBank === key ? 'selected' : ''}`}
                        onClick={() => setSelectedBank(key)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div
                            className="bank-icon-placeholder d-flex flex-column justify-content-center align-items-center"
                            style={{ flexShrink: 0 }}
                          >
                            {MethodIcon(method?.type)}
                          </div>
                          <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 'bold',
                                fontSize: 14,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {title}
                            </div>

                            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                              {subText}
                            </div>

                            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                              **** {lastFour}
                            </div>
                          </div>
                          <Radio value={key} style={{ flexShrink: 0 }} />
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Radio.Group>
          </div>
          {selectedBank && (
            <Row>
              <SupportingFields
                control={control}
                errors={errors}
                setValue={setValue}
                watch={watch}
                reset={reset}
                selectedBank={directoryPaymentMethods.find((item) => item.gid === selectedBank)}
                filteredSegmentOptions={filteredSegmentOptions}
                setSurchargeOptional={setSurchargeOptional}
                surchargeEnabled={surchargeEnabled}
                surchargeType={surchargeType}
                serviceFee={serviceFee}
                serviceFeeRate={serviceFeeRate}
                discount={discount}
                subTotal={subTotal}
                totalPayable={totalPayable}
                amount={amount}
                prefix={prefix}
                tax={tax}
                serviceFeeVal={serviceFeeVal}
                surcharge={surcharge}
                tip={tip}
                surchareNotification={surchareNotification}
              />
            </Row>
          )}
          <Row gutter={[16, 16]}>
            {
              sitekey && (isCaptchaEnabled === true) && (
                <div className="mt-3">
                  <ReCAPTCHA
                    sitekey={sitekey}
                    value={recaptchaToken}
                    onChange={(value) => {
                      setRecaptchToken(value);
                      setShowCaptchError(null);
                    }}
                  />
                  {
                    (Object.keys(errors).length > 0) && (!recaptchaToken) ? (
                      <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">Captcha is required</Text>
                    ) : showCaptchError ? (
                      <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">{showCaptchError}</Text>
                    ) : ''
                  }
                </div>
              )
            }
          </Row>
          {showAutopayCheckbox && (
            <Row className='mt-4' gutter={[16, 16]}>
              <Col xs={24}>
                <label>
                  <Checkbox
                    checked={!!autopay}
                    onChange={(e) => setAutopay && setAutopay(e.target.checked)}
                  />
                  <SpanText className="pl-2">
                    Enable Autopay for future payments
                  </SpanText>
                </label>
              </Col>
            </Row>
          )}
          <Row className='mt-5 justify-content-center' gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Button disabled={!selectedBank} style={customSubmitButtonStyle} type="primary" onClick={handleSubmit(onClickPay)}>
                { buttonText || 'PAY' }
              </Button>
            </Col>
          </Row>
        </div>
      )}
      {directoryAccessToken && directoryPaymentMethods?.length === 0 && (
        <Alert className="w-100" message="⚠️ Saved methods are not available for this payment" type="warning" />
      )}
      <Modal
        title="Verify OTP"
        open={otpModal}
        onCancel={() => { setOtpModal(false); dispatch(setSavedPaymentsClose(true)); }}
        footer={[
          <div key="footer" className="d-flex flex-column align-items-center gap-2">
            <Button loading={otpLoader} className="mb-2" type="primary" onClick={handleOk}>
              Verify
            </Button>
            <p className="mb-0">
              Didn't receive code?{" "}
              <span
                className="text-primary"
                style={{ cursor: 'pointer' }}
                onClick={handleResend}
              >
                Resend
              </span>
            </p>
          </div>,
        ]}
      >
        <div className="d-flex flex-column align-items-center">
          <p className="mb-2">Enter the 6-digit code sent to your registered mobile or email to view saved payment methods.</p>
          <div className="otp-wrapper mb-2">
            <Input.OTP length={6} size="large" type="tel" inputMode="numeric" formatter={(value) => value.replace(/\D/g, '')} onChange={setOtp} />
            <SpError fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">
              {simpleValidator.current.message('otp', otp, 'required')}
            </SpError>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SavedPaymentMethods;